import { useId } from 'react';
import { useIntl } from 'react-intl';
import {
  OFFERED_WEEKDAYS,
  normalizeWeekdays,
  partitionOffered,
  weekdayName,
} from '@/shared/dateInterest';
import { Weekday } from '@/features/accounts/types';

interface Props {
  value: Weekday[];
  onChange: (weekdays: Weekday[]) => void;
  disabled?: boolean;
}

export default function WeekdayChips({ value, onChange, disabled = false }: Props) {
  const intl = useIntl();
  const labelId = useId();
  const { offered, passthrough } = partitionOffered(value);
  const unrestricted = value.length === 0;
  const selected = unrestricted ? OFFERED_WEEKDAYS : offered;

  const toggle = (weekday: Weekday) => {
    const switchingOff = selected.includes(weekday);
    if (switchingOff && selected.length === 1) return;

    const next = switchingOff
      ? selected.filter((day) => day !== weekday)
      : [...selected, weekday];

    onChange(normalizeWeekdays(next, passthrough));
  };

  return (
    <div>
      <div className="sm:mb-1">
        <span id={labelId} className="sr-only text-xs font-semibold text-slate-700 sm:not-sr-only sm:block">
          {intl.formatMessage({ id: 'accounts.configForm.weekdaysLegend' })}
        </span>
      </div>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex min-h-9 flex-wrap items-center gap-1"
      >
        {OFFERED_WEEKDAYS.map((weekday) => (
          <button
            key={weekday}
            type="button"
            disabled={disabled}
            aria-pressed={selected.includes(weekday)}
            aria-label={weekdayName(weekday, intl.locale, 'long')}
            onClick={() => toggle(weekday)}
            className={chipClass(selected.includes(weekday), disabled)}
          >
            {weekdayName(weekday, intl.locale, 'short')}
          </button>
        ))}
      </div>
    </div>
  );
}

function chipClass(pressed: boolean, disabled: boolean): string {
  const base =
    'flex h-9 min-w-[2.75rem] items-center justify-center rounded-lg border px-3 text-sm font-medium capitalize transition focus:outline-none focus:ring-2 focus:ring-brand-200';
  if (disabled) return `${base} border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed`;
  if (!pressed) return `${base} border-slate-300 bg-white text-slate-600 hover:border-slate-400`;

  return `${base} border-brand-500 bg-brand-50 text-brand-700`;
}

